import type { QueryClient, QueryKey } from '@tanstack/react-query';
import { deleteDB, openDB, type DBSchema, type IDBPDatabase } from 'idb';
import { rememberPublicIds } from '@hlt/web-kit/api';

/**
 * Copy of the server data this person opened: audits, their answers and comments,
 * and the workspace data needed to open them, kept in IndexedDB so a reload without a connection can show
 * and continue them. It is always kept for the signed-in account (never while HLT support uses the app as a
 * member); sign-out and account switches delete everything. Only successful reads are copied; writes still
 * go through the queued, revision-checked requests. Every online read refreshes its copy, so the limits
 * below only bound storage: copies not read again for 30 days and the oldest copies beyond 25 MB are removed.
 */
export interface Entry {
    hash: string;
    actorId: string;
    organizationId: string;
    queryKey: QueryKey;
    data: unknown;
    updatedAt: number;
    /** Serialized size in characters, for the storage cap. */
    size?: number;
}

interface OfflineDb extends DBSchema {
    queries: { key: string; value: Entry };
}

const dbName = 'hlt-offline-data';
/** Copies not refreshed for this long belong to audits nobody opened for a month; they only take space. */
const maxAgeMs = 30 * 24 * 3_600_000;
/** Large lists (for example analytics) are not worth keeping; audit data is far below this. */
const maxBytes = 512_000;
/** Total budget of the copy (about 50 large audits); the least recently read copies go first. */
const maxTotalBytes = 25_000_000;

/** One connection per call, closed when it completes, so deleting the database is never blocked by this tab. */
async function withDb<T>(fn: (db: IDBPDatabase<OfflineDb>) => Promise<T>): Promise<T> {
    const db = await openDB<OfflineDb>(dbName, 1, {
        upgrade: (database) => void database.createObjectStore('queries', { keyPath: 'hash' }),
    });
    try {
        return await fn(db);
    } finally {
        db.close();
    }
}

/**
 * Reads worth keeping: everything the workspace pages load for an organization (`['api', org, ws, op, …]`,
 * see `apiKey`), evidence of audits (`['evidence', org, ws, audit, …]`), and the account-level reads the
 * shell needs offline (`['api', null, null, op, …]`).
 */
function organizationOf(key: QueryKey): string | null {
    const [kind, organizationId] = key;
    if ((kind === 'api' || kind === 'evidence') && typeof organizationId === 'string') return organizationId;
    return null;
}
const shellReads = new Set(['getSession', 'getProfile', 'listScopes', 'listMyOrganizations', 'listNotifications']);
const isShellRead = (key: QueryKey) =>
    key[0] === 'api' && key[1] === null && typeof key[3] === 'string' && shellReads.has(key[3]);

/** A copy that has not changed is rewritten at most this often, only to keep its read time for the 30-day rule. */
const refreshMs = 12 * 3_600_000;

/** What a cached read holds when the queue turns to it; null when it is gone or not worth keeping. */
export interface SnapshotRead {
    queryKey: QueryKey;
    data: unknown;
    updatedAt: number;
}

/** Runs `work` when the main thread is idle (or after a second where `requestIdleCallback` is missing). */
export type IdleScheduler = (work: (deadline: { timeRemaining: () => number }) => void) => void;

const browserIdle: IdleScheduler = (work) => {
    if (typeof requestIdleCallback === 'function') requestIdleCallback(work, { timeout: 2_000 });
    else setTimeout(() => work({ timeRemaining: () => 8 }), 1_000);
};

/**
 * Queue of reads to copy, so a read costs no main-thread time. A successful read only
 * marks its query; serialization happens later in idle time, in small slices, and a read whose data is
 * the same object as the copy already written (TanStack Query keeps the reference when a refetch returns
 * equal data) is skipped, apart from an occasional refresh of its read time.
 */
export function createSnapshotQueue({
    actorId,
    read,
    write,
    idle = browserIdle,
    now = Date.now,
}: {
    actorId: string;
    read: (hash: string) => SnapshotRead | null;
    write: (entries: Entry[]) => Promise<void>;
    idle?: IdleScheduler;
    now?: () => number;
}) {
    const dirty = new Set<string>();
    const written = new Map<string, { data: unknown; at: number }>();
    let scheduled = false;
    let stopped = false;

    const prepare = (hash: string): Entry | null => {
        const current = read(hash);
        if (!current) return null;
        const organizationId = organizationOf(current.queryKey) ?? (isShellRead(current.queryKey) ? '' : null);
        if (organizationId === null || current.data === undefined) return null;
        const last = written.get(hash);
        if (last && last.data === current.data && now() - last.at < refreshMs) return null;
        let size = 0;
        try {
            size = JSON.stringify(current.data).length;
        } catch {
            return null;
        }
        if (size > maxBytes) return null;
        written.set(hash, { data: current.data, at: now() });
        return {
            hash,
            actorId,
            organizationId,
            queryKey: current.queryKey,
            data: current.data,
            updatedAt: current.updatedAt,
            size,
        };
    };

    /** Serializes marked reads while the idle period lasts (`all` ignores it) and writes them in one transaction. */
    const drain = (deadline: { timeRemaining: () => number }, all = false) => {
        scheduled = false;
        const batch: Entry[] = [];
        for (const hash of dirty) {
            if (!all && batch.length > 0 && deadline.timeRemaining() < 2) break;
            dirty.delete(hash);
            const entry = prepare(hash);
            if (entry) batch.push(entry);
        }
        if (dirty.size && !stopped) schedule();
        return batch.length ? write(batch).catch(() => undefined) : Promise.resolve();
    };
    const schedule = () => {
        if (scheduled) return;
        scheduled = true;
        idle((deadline) => void drain(deadline));
    };

    return {
        /** Marks a read for copying; nothing is serialized here. */
        mark(hash: string) {
            if (stopped) return;
            dirty.add(hash);
            schedule();
        },
        /** Writes what is still marked, then ignores further reads. */
        stop(): Promise<void> {
            stopped = true;
            return drain({ timeRemaining: () => Infinity }, true);
        },
    };
}

/**
 * Starts copying successful reads into the offline store; returns a function that stops it. Copies are
 * written in idle time and in batches, so fast answering does not turn into main-thread work or a stream of
 * IndexedDB transactions.
 */
export function keepQueriesOffline(queryClient: QueryClient, actorId: string): () => void {
    const cache = queryClient.getQueryCache();
    const queue = createSnapshotQueue({
        actorId,
        // The cache types its queries loosely; the key comes from the query and the data stays unknown.
        read: (hash) => {
            const query = cache.get(hash);
            if (!query || query.state.status !== 'success') return null;
            const data: unknown = query.state.data;
            return { queryKey: query.queryKey, data, updatedAt: query.state.dataUpdatedAt };
        },
        write: (entries) =>
            withDb(async (db) => {
                const tx = db.transaction('queries', 'readwrite');
                await Promise.all([...entries.map((entry) => tx.store.put(entry)), tx.done]);
            }),
    });
    void pruneOfflineQueries(actorId).catch(() => undefined);
    // Reads made before the copy started (the session, the workspace, the page that was open) count too.
    for (const query of cache.getAll()) if (query.state.status === 'success') queue.mark(query.queryHash);
    const unsubscribe = cache.subscribe((event) => {
        if (event.type === 'updated' && event.action.type === 'success') queue.mark(event.query.queryHash);
    });
    return () => {
        unsubscribe();
        void queue.stop();
    };
}

const tooOld = (entry: Entry, now: number) => now - entry.updatedAt > maxAgeMs;

/**
 * Removes copies of other accounts, copies older than 30 days and, beyond the total budget, the least
 * recently read copies. Runs once when copying starts, not on every write.
 */
async function pruneOfflineQueries(actorId: string): Promise<void> {
    const now = Date.now();
    await withDb(async (db) => {
        const tx = db.transaction('queries', 'readwrite');
        const all = await tx.store.getAll();
        const keep = all
            .filter((entry) => entry.actorId === actorId && !tooOld(entry, now))
            .sort((a, b) => b.updatedAt - a.updatedAt);
        let total = 0;
        const kept = new Set<string>();
        for (const entry of keep) {
            total += entry.size ?? JSON.stringify(entry.data).length;
            if (total > maxTotalBytes) break;
            kept.add(entry.hash);
        }
        const removed = all.filter((entry) => !kept.has(entry.hash));
        await Promise.all([...removed.map((entry) => tx.store.delete(entry.hash)), tx.done]);
    });
}

/**
 * Puts this person's copies into the query cache, marked with the time they were read, so the
 * app can open them without a connection. Fresh data replaces them as soon as requests succeed again.
 */
export async function restoreOfflineQueries(queryClient: QueryClient, actorId: string): Promise<number> {
    const now = Date.now();
    let entries: Entry[];
    try {
        // Copies of another account or older than 30 days are deleted while reading.
        entries = await withDb(async (db) => {
            const tx = db.transaction('queries', 'readwrite');
            const all = await tx.store.getAll();
            const stale = all.filter((entry) => entry.actorId !== actorId || tooOld(entry, now));
            await Promise.all([...stale.map((entry) => tx.store.delete(entry.hash)), tx.done]);
            return all.filter((entry) => !stale.includes(entry));
        });
    } catch {
        return 0;
    }
    let restored = 0;
    for (const entry of entries) {
        if (queryClient.getQueryData(entry.queryKey) !== undefined) continue;
        queryClient.setQueryData(entry.queryKey, entry.data, { updatedAt: entry.updatedAt });
        // Short addresses of what was opened before resolve without a connection.
        rememberPublicIds(entry.data);
        restored += 1;
    }
    return restored;
}

/** Removes every offline copy (sign-out, account switch, or HLT support using the app as a member). */
export async function clearOfflineQueries(): Promise<void> {
    // Another tab holding the database delays the deletion until it closes; nothing to wait for here.
    await new Promise<void>((resolve, reject) => {
        deleteDB(dbName, { blocked: () => resolve() }).then(resolve, reject);
    });
}
